import React, { useState } from 'react';

import { Button, Col, Row } from 'antd';

import {
  CloseCircleOutlined,
  CopyOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import {
  FormItemContainer,
  GroupElementWrapper,
  HeaderGroupElement,
  LabelFormItem,
} from 'pages/product-full-view/shared/components';

import { FormPrimitiveItemOverlay } from './FormItemOverlayItem';
import { FormMultipleNestedValueOverlay } from './FormMultipleNestedValueOverlay';

import {
  productDetailActionTypes,
  useProductDetail,
  useProductDetailDispatch,
} from '../ProductDetailContext';

import {
  checkIsValidObjectValues,
  checkTypeOfFormNested,
  getFieldValueFromListIdx,
  getFormNameItem,
  getValuesAfterDeletingSingleNestedForm,
} from '../utils';

import { FormAddButton, dialogFunction } from 'common/components';
import { generateOriginalStringPath } from './utils';

export const FormSingleNestedValueOverlay = ({
  name,
  property,
  parentNestedIndex = [],
  productEnums,
  handleValidate,
  originalProductModules,
}) => {
  const [action, setAction] = useState('idle');

  const { formInstance } = useProductDetail();

  const dispatch = useProductDetailDispatch();

  const initialFormValues = formInstance.getFieldValue(parentNestedIndex); // use parentNestedIndex to get values because values of form is nested object

  const hasInitialValues = Boolean(initialFormValues);

  const isAddedValuesProperty = action === 'add' || hasInitialValues;
  const isDisabledBtn = isAddedValuesProperty && action !== 'delete';

  const {
    propertyName,
    propertyDisplayName,
    propertyDescription,
    fieldFullPath,
    childProperties,
  } = property;

  const handleDeleteFieldForm = () => {
    setAction('delete');
    const newFormValues = getValuesAfterDeletingSingleNestedForm({
      formValues: formInstance.getFieldsValue(),
      parentNestedIndex,
      propertyName,
    });
    formInstance.setFieldsValue(newFormValues);
  };

  const handleUpdateDeletedFieldsOverlay = () => {
    const originalFieldFormValues = getFieldValueFromListIdx(
      originalProductModules,
      parentNestedIndex
    );

    if (
      originalFieldFormValues === null ||
      originalFieldFormValues === undefined
    ) {
      return;
    }

    dispatch({
      type: productDetailActionTypes.FILTER_DELETED_FIELDS_OVERLAY,
      filteredParentPath: generateOriginalStringPath(parentNestedIndex),
    });
  };

  const confirmDeleteFieldForm = () => {
    const formValues = formInstance.getFieldsValue();
    const fieldFormValues = getFieldValueFromListIdx(
      formValues,
      parentNestedIndex
    );

    const isValidValues = checkIsValidObjectValues(fieldFormValues);

    if (isValidValues) {
      dialogFunction({
        type: 'warn',
        content: 'Are you sure you want to delete selected property?',
        okText: 'Delete',
        okButtonProps: {
          type: 'danger',
        },
        onOk: () => {
          handleDeleteFieldForm();
          handleUpdateDeletedFieldsOverlay();
        },
      });
    } else {
      handleDeleteFieldForm();
      handleUpdateDeletedFieldsOverlay();
    }
  };

  const renderChildElement = ({ property, name, originalProductModules }) => {
    const { propertyName, propertyDisplayName, isArray, childProperties } =
      property;

    const nameFormItem = getFormNameItem(name, propertyName);

    const { isSingleNestedValue, isMultipleNestedValue } =
      checkTypeOfFormNested(childProperties, isArray);

    if (isSingleNestedValue) {
      const singleParentNestedIndex = parentNestedIndex.concat(propertyName);

      return (
        <FormSingleNestedValueOverlay
          name={nameFormItem}
          property={property}
          productEnums={productEnums}
          parentNestedIndex={singleParentNestedIndex}
          handleValidate={handleValidate}
          originalProductModules={originalProductModules}
        />
      );
    }

    if (isMultipleNestedValue) {
      return (
        <FormMultipleNestedValueOverlay
          properties={childProperties}
          isArray={isArray}
          name={nameFormItem}
          label={propertyDisplayName}
          property={property}
          productEnums={productEnums}
          parentNestedIndex={parentNestedIndex}
          handleValidate={handleValidate}
          originalProductModules={originalProductModules}
        />
      );
    } else {
      return (
        <FormPrimitiveItemOverlay
          nameFormItem={nameFormItem}
          productEnums={productEnums}
          parentNestedIndex={parentNestedIndex}
          property={property}
          originalProductModules={originalProductModules}
        />
      );
    }
  };

  return (
    <>
      <FormItemContainer name={name}>
        <Row style={{ marginBottom: 6 }}>
          <Col
            span={6}
            style={{
              padding: '4px 0px',
              textAlign: 'right',
              marginLeft: 10,
            }}
            className='product-detail__label-form'
          >
            <LabelFormItem
              label={propertyDisplayName}
              tooltip={propertyDescription}
              fieldFullPath={fieldFullPath}
            />
          </Col>
          <Col>
            <FormAddButton
              type='secondary'
              icon={<PlusOutlined />}
              text='Add value'
              onClick={() => {
                setAction('add');
              }}
              disabled={isDisabledBtn}
            />
          </Col>
        </Row>
      </FormItemContainer>
      {isAddedValuesProperty ? (
        <Row
          style={{
            position: 'relative',
            marginTop: 15,
          }}
          wrap={false}
        >
          <GroupElementWrapper
            flex='auto'
            style={{ marginLeft: 24, marginBottom: 6 }}
            className='product-detail__group-element'
          >
            <HeaderGroupElement header={propertyDisplayName} />

            {childProperties.map((childProperty) => {
              return (
                <React.Fragment key={childProperty.fieldFullPath}>
                  {renderChildElement({
                    property: childProperty,
                    name,
                    originalProductModules,
                  })}
                </React.Fragment>
              );
            })}
          </GroupElementWrapper>

          <Col flex='24px' style={{ marginLeft: 4, marginRight: 4 }}>
            <Button
              type='danger'
              icon={<CloseCircleOutlined />}
              style={{
                marginBottom: 4,
                borderColor: '#ff4d4f',
                backgroundColor: '#ff4d4f',
                borderRadius: 4,
              }}
              onClick={confirmDeleteFieldForm}
            />

            <Button
              type='primary'
              className='product-detail__btn-copy'
              icon={<CopyOutlined />}
              disabled={true}
            />
          </Col>
        </Row>
      ) : null}
    </>
  );
};
